"use client";

import Image from "next/image";
import { AnimateSection, StaggerContainer, StaggerItem } from "@/components/AnimateSection";
import { SkillBar } from "@/components/SkillBar";
import { TerminalWindow } from "@/components/TerminalWindow";
import { ExperienceTimeline } from "@/components/ExperienceTimeline";
import { CertificateGallery } from "@/components/CertificateGallery";
import { GlowingCard } from "@/components/GlowingCard";
import { SmoothCounter } from "@/components/SmoothCounter";
import { GitHubDashboard } from "@/components/GitHubDashboard";
import {
  useProfile,
  useExperiences,
  useEducation,
  useSkills,
  useCertificates,
} from "@/lib/use-portfolio-data";
import { useGitHubStats } from "@/lib/use-github-stats";
import { useI18n } from "@/i18n/I18nProvider";

const INTERESTS = [
  { icon: "📚", label: "Reading", desc: "Tech books & documentation" },
  { icon: "🎮", label: "Gaming", desc: "Strategy & simulation" },
  { icon: "🌐", label: "Open Source", desc: "Contributing & learning" },
  { icon: "☁️", label: "Cloud & DevOps", desc: "Docker, Jenkins, CI/CD" },
];

export default function AboutPage() {
  const { profile, loading: profileLoading } = useProfile();
  const { data: experiences } = useExperiences();
  const { data: educationList } = useEducation();
  const { data: skills } = useSkills();
  const { data: certificates } = useCertificates();
  const { data: githubStats, loading: githubLoading } = useGitHubStats(
    profile?.github_url,
    profile?.github_token
  );
  const { t } = useI18n();

  const stats =
    profile && githubStats
      ? [
        { key: "openSourceRepos", value: githubStats.public_repos, suffix: "+" },
        { key: "monthsOfExperience", value: profile.stat_months_exp, suffix: "+" },
        { key: "githubStars", value: githubStats.total_stars, suffix: "+" },
        { key: "commits", value: githubStats.total_commits, suffix: "+" },
      ]
      : profile
        ? [
          { key: "projects", value: profile.stat_projects, suffix: "+" },
          { key: "monthsOfExperience", value: profile.stat_months_exp, suffix: "+" },
          { key: "technologies", value: profile.stat_technologies, suffix: "+" },
          { key: "productionCommits", value: profile.stat_commits, suffix: "+" },
        ]
        : [];

  if (profileLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-dev-cyan border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-16 sm:py-20">
      {/* Header */}
      <AnimateSection>
        <h1 className="text-4xl font-bold text-dev-text-primary sm:text-5xl">
          {t("about.title")}
        </h1>
        <p className="mt-4 max-w-2xl text-lg text-dev-text-muted leading-relaxed">
          {profile?.hero_description || profile?.bio || ""}
        </p>
      </AnimateSection>

      {/* Personal Intro Card */}
      {profile && (
        <AnimateSection className="mt-12" delay={0.1}>
          <div className="clay-card p-8 sm:p-10 gradient-border">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
              {profile.avatar_url && (
                <div className="flex-shrink-0 mx-auto sm:mx-0">
                  <div className="avatar-ring h-24 w-24 overflow-hidden rounded-full">
                    <Image
                      src={profile.avatar_url}
                      alt={profile.name}
                      width={96}
                      height={96}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>
              )}
              <div>
                <h2 className="text-xl font-semibold text-dev-text-primary">{profile.name}</h2>
                <p className="mt-1 font-mono text-sm text-dev-cyan">{profile.title}</p>
                {profile.bio && (
                  <p className="mt-4 text-dev-text-secondary leading-relaxed">{profile.bio}</p>
                )}
                <div className="mt-4 flex flex-wrap gap-3 text-sm text-dev-text-muted">
                  {profile.address && (
                    <span className="flex items-center gap-1.5">📍 {profile.address}</span>
                  )}
                  {profile.email && (
                    <span className="flex items-center gap-1.5">📧 {profile.email}</span>
                  )}
                  {profile.phone && (
                    <span className="flex items-center gap-1.5">📱 {profile.phone}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </AnimateSection>
      )}

      {/* Terminal */}
      <AnimateSection className="mt-12" delay={0.12}>
        <TerminalWindow />
      </AnimateSection>

      {/* GitHub Activity (chi tiết) */}
      {profile?.github_url && (
        <AnimateSection className="mt-16" delay={0.14}>
          <h2 className="mb-3 text-2xl font-bold text-dev-text-primary sm:text-3xl">
            {t("about.githubActivityTitle")}
          </h2>
          <p className="mb-6 max-w-2xl text-sm text-dev-text-muted">
            {t("about.githubActivitySubtitle")}
          </p>
          <GitHubDashboard
            githubUrl={profile.github_url}
            cachedData={githubStats}
            loading={githubLoading}
            token={profile.github_token}
          />
        </AnimateSection>
      )}

      {/* Skills */}
      {skills.length > 0 && (
        <AnimateSection className="mt-16" delay={0.15}>
          <h2 className="mb-8 text-2xl font-bold text-dev-text-primary sm:text-3xl">
            {t("about.skillsTitle")}
          </h2>
          <div className="clay-card p-8 space-y-5">
            {skills.map((skill) => (
              <SkillBar
                key={skill.id}
                name={skill.name}
                level={skill.level}
                icon={skill.icon}
                color={skill.color}
              />
            ))}
          </div>
        </AnimateSection>
      )}

      {/* Experience */}
      {experiences.length > 0 && (
        <AnimateSection className="mt-16" delay={0.18}>
          <h2 className="mb-8 text-2xl font-bold text-dev-text-primary sm:text-3xl">
            {t("about.experienceTitle")}
          </h2>
          <ExperienceTimeline experiences={experiences} />
        </AnimateSection>
      )}

      {/* Education */}
      {educationList.length > 0 && (
        <AnimateSection className="mt-16" delay={0.2}>
          <h2 className="mb-8 text-2xl font-bold text-dev-text-primary sm:text-3xl">
            {t("about.educationTitle")}
          </h2>
          <StaggerContainer className="space-y-4">
            {educationList.map((edu) => (
              <StaggerItem key={edu.id} as="div">
                <div className="clay-card p-6 sm:p-8">
                  <div className="flex items-start gap-4">
                    <span className="text-3xl flex-shrink-0 mt-1">{edu.icon}</span>
                    <div>
                      <h3 className="text-lg font-semibold text-dev-text-primary">{edu.school}</h3>
                      <p className="mt-1 text-sm text-dev-cyan">{edu.degree}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-dev-text-muted">
                        <span className="font-mono">{edu.period}</span>
                        {edu.grade && <span className="tech-tag">{edu.grade}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </AnimateSection>
      )}

      {/* Certificates */}
      <AnimateSection className="mt-16" delay={0.22}>
        <h2 className="mb-8 text-2xl font-bold text-dev-text-primary sm:text-3xl">
          {t("about.certificatesTitle")}
        </h2>
        <CertificateGallery certificates={certificates} />
      </AnimateSection>

      {/* Interests */}
      <AnimateSection className="mt-16" delay={0.25}>
        <h2 className="mb-8 text-2xl font-bold text-dev-text-primary sm:text-3xl">
          {t("about.interestsTitle")}
        </h2>
        <StaggerContainer className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {INTERESTS.map((item) => (
            <StaggerItem key={item.label} as="div">
              <div className="clay-card group p-5 text-center h-full">
                <span className="text-3xl block mb-3 transition-transform duration-300 group-hover:scale-110">
                  {item.icon}
                </span>
                <h3 className="text-sm font-semibold text-dev-text-primary">{item.label}</h3>
                <p className="mt-1 text-xs text-dev-text-muted">{item.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </AnimateSection>
    </div>
  );
}
